
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import React, { useEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import clsxm from '@/lib/clsxm';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import Seo from '@/components/Seo';
import Turnstile, { isTurnstileEnabled, type TurnstileHandle, } from '@/components/Turnstile';
import { CREATOR_APPLICATION_TURNSTILE_ACTION } from '@/constants/turnstile';
import httpClient from '@/utils/httpClient';
/* ─── Lazy embed utilities ─── */
const scriptCache = new Map<string, Promise<void>>();
function loadScript(src: string): Promise<void> {
    if (scriptCache.has(src))
        return scriptCache.get(src)!;
    const promise = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = reject;
        document.body.appendChild(script);
    });
    scriptCache.set(src, promise);
    return promise;
}
function useLazyEmbed() {
    const ref = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        const obs = new IntersectionObserver(([e]) => {
            if (e.isIntersecting) {
                setIsVisible(true);
                obs.disconnect();
            }
        }, { rootMargin: '200px' });
        obs.observe(el);
        return () => obs.disconnect();
    }, []);
    return { ref, isVisible };
}
const EMBED_CLASS = 'flex-shrink-0 w-[min(100%,326px)] sm:w-[350px]';
const SKELETON_CLASS = 'aspect-[9/16] w-full animate-pulse rounded-xl bg-neutral-100 dark:bg-neutral-800';
function InstagramEmbed({ postUrl }: {
    postUrl: string;
}) {
    const { ref, isVisible } = useLazyEmbed();
    useEffect(() => {
        if (!isVisible)
            return;
        loadScript('https://www.instagram.com/embed.js').then(() => {
            (window as any).instgrm?.Embeds?.process();
        });
    }, [isVisible]);
    return (<div ref={ref} className={EMBED_CLASS}>
      {isVisible ? (<blockquote className='instagram-media' data-instgrm-captioned data-instgrm-permalink={postUrl} data-instgrm-version='14' style={{ maxWidth: 540, width: '100%', margin: 0 }}/>) : (<div className={SKELETON_CLASS}/>)}
    </div>);
}
// TikTok's embed.js only processes blockquotes present at script load time.
// Debounced reload ensures a single re-injection after all visible embeds mount.
let tiktokReloadTimer: ReturnType<typeof setTimeout> | null = null;
function debouncedTikTokReload() {
    if (tiktokReloadTimer)
        clearTimeout(tiktokReloadTimer);
    tiktokReloadTimer = setTimeout(() => {
        const existing = document.querySelector('script[src="https://www.tiktok.com/embed.js"]');
        if (existing)
            existing.remove();
        scriptCache.delete('https://www.tiktok.com/embed.js');
        loadScript('https://www.tiktok.com/embed.js');
    }, 200);
}
function TikTokEmbed({ videoId, username, }: {
    videoId: string;
    username: string;
}) {
    const { ref, isVisible } = useLazyEmbed();
    useEffect(() => {
        if (!isVisible)
            return;
        debouncedTikTokReload();
    }, [isVisible]);
    return (<div ref={ref} className={EMBED_CLASS}>
      {isVisible ? (<blockquote className='tiktok-embed' cite={`https://www.tiktok.com/@${username}/video/${videoId}`} data-video-id={videoId} style={{ maxWidth: 605, minWidth: 325 }}>
          <section />
        </blockquote>) : (<div className={SKELETON_CLASS}/>)}
    </div>);
}
const creatorEmbeds = {
    instagram: [
        'https://www.instagram.com/reel/DWT_ftfkVXe/',
        'https://www.instagram.com/reel/DSXG8v6jesr/',
    ],
    tiktok: [
        { username: 'toptech_oficial', videoId: '7431963284909116677' },
        { username: 'shedoesai', videoId: '7313916622081772832' },
        { username: 'ai_tips_for_you', videoId: '7585077910784970005' },
        { username: 'maherzaou', videoId: '7369655275264986374' },
        { username: 'seanseeksalha', videoId: '7215015599670447378' },
        { username: 'engenheirodotrafego_', videoId: '7314719297685703941' },
        { username: 'adam.digital', videoId: '7563279355141442823' },
        { username: 'fachrimirza', videoId: '7321708483919891717' },
        { username: 'ai_tips_for_you', videoId: '7354349199254654224' },
    ],
};
type CreatorFormInputs = {
    email: string;
    socialLinks: string;
};
const benefits = [
    {
        num: '01',
        titleKey: 'Early access to every new AI model',
        descKey: 'Chat, image, video, audio — you get it first. Before the public, before the hype.',
        accent: 'from-violet-500 to-fuchsia-500',
    },
    {
        num: '02',
        titleKey: 'A Pro plan, on us',
        descKey: 'Full access to every tool on the platform. No credit limits holding you back.',
        accent: 'from-blue-500 to-cyan-500',
    },
    {
        num: '03',
        titleKey: '30% revenue share',
        descKey: 'Recurring commissions on every subscriber you bring in. Passive income that compounds.',
        accent: 'from-emerald-500 to-teal-500',
    },
    {
        num: '04',
        titleKey: 'Your content, amplified',
        descKey: 'We feature creators across our blog, newsletter, and socials — reaching millions of users.',
        accent: 'from-amber-500 to-orange-500',
    },
    {
        num: '05',
        titleKey: 'Shape the product',
        descKey: 'Direct line to our product team. Your feedback drives what we build next.',
        accent: 'from-rose-500 to-pink-500',
    },
    {
        num: '06',
        titleKey: 'A partner, not a program',
        descKey: 'Dedicated support, private community, and a real person who knows your name.',
        accent: 'from-sky-500 to-indigo-500',
    },
];
const capabilities = [
    {
        labelKey: 'Chat & Writing',
        descKey: 'The most advanced LLMs from OpenAI, Anthropic, Google, Meta, DeepSeek, Mistral, and more. 100+ templates. Custom chatbots.',
        stat: '20+',
        statLabel: 'AI models',
    },
    {
        labelKey: 'Image Generation',
        descKey: '40+ models from the leading AI labs. Photorealistic, artistic, product shots — whatever you imagine.',
        stat: '40+',
        statLabel: 'image models',
    },
    {
        labelKey: 'Video & Audio',
        descKey: 'Text-to-video, talking avatars, voice cloning, music generation, sound effects. One platform, every modality.',
        stat: '10+',
        statLabel: 'video models',
    },
];
const faqs = [
    {
        q: 'Who can apply?',
        a: 'Anyone who creates content — whether you have 100 followers or 1 million. YouTubers, TikTokers, Instagram creators, Twitter/X users, bloggers, podcasters, educators, digital artists. We care about your passion for creating, not your follower count.',
    },
    {
        q: 'How much can I earn?',
        a: 'Up to 30% recurring commission on every paying subscriber you refer. Top creators earn thousands per month. Plus, you get a free Pro plan.',
    },
    {
        q: 'How long until I hear back?',
        a: "We review every application within 48 hours. You'll get an email with your approval status and next steps.",
    },
    {
        q: 'What kind of content should I create?',
        a: 'Whatever feels authentic to you. Tutorials, reviews, comparisons, workflow demos, before-and-after showcases — you have full creative freedom. We provide ideas and assets to help.',
    },
    {
        q: 'Is there a posting minimum?',
        a: 'We ask for 1-2 pieces of content per month. No strict quota — quality over volume, always.',
    },
    {
        q: 'Already an affiliate?',
        a: 'The Creator Program stacks on top — free Pro access, early features, co-marketing, dedicated support. Everything you have, plus more.',
    },
];
/* ─── Marquee of AI provider names ─── */
const providerNames = [
    'OpenAI',
    'Anthropic',
    'Google',
    'Meta',
    'Mistral',
    'DeepSeek',
    'ByteDance',
    'Stability AI',
    'Black Forest Labs',
    'Replicate',
    'Fal.ai',
    'Ideogram',
    'Recraft',
];
function Marquee() {
    const { t } = useLocale('creators');
    return (<div className='relative overflow-hidden py-6'>
      {/* Fade edges */}
      <div className='pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-white dark:from-[#0c0c0c]'/>
      <div className='pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-white dark:from-[#0c0c0c]'/>
      <div className='flex animate-marquee items-center gap-8'>
        {[...providerNames, ...providerNames].map((name, i) => (<span key={`${name}-${i}`} className='shrink-0 whitespace-nowrap text-sm font-medium text-neutral-400 dark:text-neutral-600'>
            {t(name)}
          </span>))}
      </div>
      <style jsx>{`
        @keyframes marquee {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(-50%);
          }
        }
        .animate-marquee {
          animation: marquee 25s linear infinite;
          width: max-content;
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-marquee {
            animation: none;
          }
        }
      `}</style>
    </div>);
}
/* ─── Animated counter ─── */
function AnimatedStat({ value, label }: {
    value: string;
    label: string;
}) {
    const ref = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);
    const numericPart = value.replace(/\D/g, '');
    const suffix = value.replace(/\d/g, '');
    const [count, setCount] = useState(0);
    useEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        const obs = new IntersectionObserver(([e]) => {
            if (e.isIntersecting) {
                setVisible(true);
                obs.disconnect();
            }
        }, { threshold: 0.5 });
        obs.observe(el);
        return () => obs.disconnect();
    }, []);
    useEffect(() => {
        if (!visible)
            return;
        const target = parseInt(numericPart, 10);
        const duration = 800;
        const start = performance.now();
        const step = (now: number) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.round(eased * target));
            if (progress < 1)
                requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
    }, [visible, numericPart]);
    return (<div ref={ref}>
      <span className='block text-4xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-5xl'>
        {count}
        {suffix}
      </span>
      <span className='mt-1 block text-sm text-neutral-500'>{label}</span>
    </div>);
}
const SCROLL_BTN_CLASS = 'flex h-9 w-9 items-center justify-center rounded-full border border-neutral-200 text-neutral-500 transition-colors hover:border-neutral-400 hover:text-neutral-900 dark:border-neutral-700 dark:hover:border-neutral-500 dark:hover:text-white';
function CreatorSpotlight() {
    const { t } = useLocale('creators');
    const scrollRef = useRef<HTMLDivElement>(null);
    const scroll = (dir: 'left' | 'right') => {
        scrollRef.current?.scrollBy({
            left: dir === 'left' ? -370 : 370,
            behavior: 'smooth',
        });
    };
    return (<section className='relative border-y border-neutral-200 bg-neutral-50 py-24 dark:border-neutral-800 dark:bg-neutral-900/30 sm:py-32'>
      <div className='mx-auto max-w-7xl px-5 sm:px-8'>
        <div className='mb-10 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between'>
          <div>
            <p className='mb-3 text-sm font-medium uppercase tracking-[.25em] text-neutral-500'>
              {t('Spotlight')}
            </p>
            <h2 className='text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl'>
              {t('Creators already loving it.')}
            </h2>
          </div>

          <div className='hidden items-center gap-1 sm:flex'>
            <button onClick={() => scroll('left')} aria-label='Scroll left' className={SCROLL_BTN_CLASS}>
              <ChevronLeftIcon className='h-4 w-4'/>
            </button>
            <button onClick={() => scroll('right')} aria-label='Scroll right' className={SCROLL_BTN_CLASS}>
              <ChevronRightIcon className='h-4 w-4'/>
            </button>
          </div>
        </div>

        <div ref={scrollRef} className='-mx-5 flex snap-x snap-mandatory gap-5 overflow-x-auto px-5 pb-4 scrollbar-hide sm:-mx-0 sm:px-0'>
          {creatorEmbeds.tiktok.map((tt) => (<TikTokEmbed key={tt.videoId} videoId={tt.videoId} username={tt.username}/>))}
          {creatorEmbeds.instagram.map((url) => (<InstagramEmbed key={url} postUrl={url}/>))}
        </div>
      </div>
    </section>);
}
export default function CreatorsPage() {
    const { t } = useLocale('creators');
    const [isLoading, setIsLoading] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error' | 'captcha'>('idle');
    const turnstileRef = useRef<TurnstileHandle>(null);
    const [openFaq, setOpenFaq] = useState<number | null>(null);
    const formRef = useRef<HTMLDivElement>(null);
    const { register, handleSubmit, reset, formState: { errors }, } = useForm<CreatorFormInputs>();
    const onSubmit: SubmitHandler<CreatorFormInputs> = async (data) => {
        setIsLoading(true);
        setSubmitStatus('idle');
        try {
            const turnstileToken = await turnstileRef.current?.getToken();
            if (isTurnstileEnabled && !turnstileToken) {
                setSubmitStatus('captcha');
                return;
            }
            await httpClient.post('/api/creator-application', JSON.stringify({ ...data, turnstileToken }), { skipErrorToast: true });
            setSubmitStatus('success');
            reset();
        }
        catch {
            setSubmitStatus('error');
        }
        finally {
            // Tokens are single-use — refresh for the next attempt either way.
            turnstileRef.current?.reset();
            setIsLoading(false);
        }
    };
    const scrollToForm = () => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    return (<main className='bg-white dark:bg-[#0c0c0c]'>
      <Seo templateTitle={t('Creator Program | Easy-Peasy.AI')} description={t('Join the Easy-Peasy.AI Creator Program. Early access to AI tools, free Pro plan, 30% revenue share, and co-marketing for content creators.')} image={`https://easy-peasy.ai/api/og-template?title=${encodeURIComponent(t('Creator Program'))}`}/>

      <LandingPageNavigation />

      {/* ━━━ HERO ━━━ */}
      <section className='relative overflow-hidden pb-16 pt-32 sm:pb-24 sm:pt-44 lg:pb-28 lg:pt-52'>
        {/* Subtle mesh background */}
        <div className='pointer-events-none absolute inset-0 overflow-hidden'>
          <div className='absolute -right-1/4 top-0 h-[600px] w-[600px] rounded-full bg-violet-100 opacity-40 blur-[120px] dark:bg-violet-900/20'/>
          <div className='absolute -left-1/4 top-1/3 h-[400px] w-[400px] rounded-full bg-sky-100 opacity-40 blur-[120px] dark:bg-sky-900/20'/>
        </div>

        <div className='relative mx-auto max-w-7xl px-5 sm:px-8'>
          <div className='mx-auto max-w-3xl'>
            {/* Kicker with dot */}
            <div className='mb-8 flex items-center gap-3'>
              <span className='relative flex h-2 w-2'>
                <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75'/>
                <span className='relative inline-flex h-2 w-2 rounded-full bg-emerald-500'/>
              </span>
              <p className='text-sm font-medium uppercase tracking-[.25em] text-neutral-500 dark:text-neutral-500'>
                {t('Now accepting applications')}
              </p>
            </div>

            {/* Headline */}
            <h1 className='text-[clamp(2.5rem,6vw,4.5rem)] font-bold leading-[1.05] tracking-tight text-neutral-900 dark:text-white'>
              {t('We give creators the best AI tools on the planet.')}{' '}
              <span className='bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent dark:from-violet-400 dark:to-fuchsia-400'>
                {t('You make them famous.')}
              </span>
            </h1>

            {/* Subhead */}
            <p className='mt-8 max-w-xl text-lg leading-relaxed text-neutral-600 dark:text-neutral-400'>
              {t('Early access to every model. A free Pro plan. Revenue share. Co-marketing. All we ask is that you create.')}
            </p>

            {/* CTA */}
            <div className='mt-10 flex items-center gap-5'>
              <button onClick={scrollToForm} className='group relative overflow-hidden rounded-full bg-neutral-900 px-7 py-3.5 text-[15px] font-semibold text-white transition-all hover:shadow-lg hover:shadow-neutral-900/20 active:scale-[.97] dark:bg-white dark:text-neutral-900 dark:hover:shadow-white/10'>
                <span className='relative z-10'>{t('Apply now')}</span>
              </button>
              <a href='#how-it-works' className='text-[15px] font-medium text-neutral-600 underline decoration-neutral-300 underline-offset-4 transition-colors hover:text-neutral-900 dark:text-neutral-400 dark:decoration-neutral-700 dark:hover:text-white'>
                {t('How it works')}
              </a>
            </div>
          </div>
        </div>

        {/* Provider marquee */}
        <div className='relative mt-16 sm:mt-20'>
          <Marquee />
        </div>
      </section>

      {/* ━━━ STATS RIBBON ━━━ */}
      <section className='border-y border-neutral-200 py-12 dark:border-neutral-800 sm:py-16'>
        <div className='mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-12 px-5 sm:gap-20 sm:px-8'>
          <AnimatedStat value='3M+' label={t('users on the platform')}/>
          <div className='hidden h-12 w-px bg-neutral-200 dark:bg-neutral-800 sm:block'/>
          <AnimatedStat value='40+' label={t('image generation models')}/>
          <div className='hidden h-12 w-px bg-neutral-200 dark:bg-neutral-800 sm:block'/>
          <AnimatedStat value='20+' label={t('chat & LLM models')}/>
          <div className='hidden h-12 w-px bg-neutral-200 dark:bg-neutral-800 sm:block'/>
          <AnimatedStat value='30%' label={t('recurring commission')}/>
        </div>
      </section>

      {/* ━━━ WHAT YOU GET ━━━ */}
      <section className='relative py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-5 sm:px-8'>
          <div className='mb-16 max-w-2xl'>
            <p className='mb-3 text-sm font-medium uppercase tracking-[.25em] text-neutral-500'>
              {t('Benefits')}
            </p>
            <h2 className='text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl'>
              {t('Everything you need to create and earn.')}
            </h2>
          </div>

          <div className='grid gap-px overflow-hidden rounded-2xl border border-neutral-200 bg-neutral-200 dark:border-neutral-800 dark:bg-neutral-800 sm:grid-cols-2 lg:grid-cols-3'>
            {benefits.map((b) => (<div key={b.num} className='group relative flex flex-col overflow-hidden bg-white p-8 transition-colors hover:bg-neutral-50 dark:bg-[#0c0c0c] dark:hover:bg-neutral-900/50 sm:p-10'>
                {/* Accent bar on hover */}
                <div className={clsxm('absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r opacity-0 transition-opacity duration-300 group-hover:opacity-100', b.accent)}/>
                <span className='mb-6 text-xs font-semibold tabular-nums text-neutral-400 dark:text-neutral-600'>
                  {b.num}
                </span>
                <h3 className='mb-3 text-lg font-semibold leading-snug text-neutral-900 dark:text-white'>
                  {t(b.titleKey)}
                </h3>
                <p className='text-[15px] leading-relaxed text-neutral-500 dark:text-neutral-400'>
                  {t(b.descKey)}
                </p>
              </div>))}
          </div>
        </div>
      </section>

      {/* ━━━ AI CAPABILITIES ━━━ */}
      <section className='relative border-y border-neutral-200 bg-neutral-50 py-24 dark:border-neutral-800 dark:bg-neutral-900/30 sm:py-32'>
        <div className='mx-auto max-w-7xl px-5 sm:px-8'>
          <div className='mb-16 max-w-2xl'>
            <p className='mb-3 text-sm font-medium uppercase tracking-[.25em] text-neutral-500'>
              {t('The tools')}
            </p>
            <h2 className='text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl'>
              {t('One platform. Every leading AI provider.')}
            </h2>
            <p className='mt-4 text-lg text-neutral-600 dark:text-neutral-400'>
              {t('Access models the moment they launch — no switching tools, no extra subscriptions.')}
            </p>
          </div>

          <div className='grid gap-6 lg:grid-cols-3'>
            {capabilities.map((cap, i) => (<div key={i} className='group flex flex-col justify-between rounded-2xl border border-neutral-200 bg-white p-8 transition-shadow hover:shadow-lg hover:shadow-neutral-200/50 dark:border-neutral-800 dark:bg-[#0c0c0c] dark:hover:shadow-neutral-900/50 sm:p-10'>
                <div>
                  <span className='mb-6 inline-block rounded-full bg-neutral-100 px-3.5 py-1 text-xs font-semibold text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300'>
                    {t(cap.labelKey)}
                  </span>
                  <p className='text-[15px] leading-relaxed text-neutral-600 dark:text-neutral-400'>
                    {t(cap.descKey)}
                  </p>
                </div>
                <div className='mt-8 border-t border-neutral-100 pt-6 dark:border-neutral-800'>
                  <span className='text-3xl font-bold tracking-tight text-neutral-900 dark:text-white'>
                    {cap.stat}
                  </span>
                  <span className='ml-2 text-sm text-neutral-500'>
                    {t(cap.statLabel)}
                  </span>
                </div>
              </div>))}
          </div>
        </div>
      </section>

      {/* ━━━ HOW IT WORKS ━━━ */}
      <section id='how-it-works' className='relative py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-5 sm:px-8'>
          <div className='mb-16 max-w-2xl'>
            <p className='mb-3 text-sm font-medium uppercase tracking-[.25em] text-neutral-500'>
              {t('Process')}
            </p>
            <h2 className='text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl'>
              {t('Three steps. Two minutes.')}
            </h2>
          </div>

          <div className='grid gap-12 lg:grid-cols-3 lg:gap-8'>
            {[
            {
                step: '1',
                titleKey: 'Apply',
                descKey: "Drop your email and a link to your content. That's it.",
                color: 'bg-violet-500',
            },
            {
                step: '2',
                titleKey: 'Get approved',
                descKey: 'We review every application within 48 hours.',
                color: 'bg-sky-500',
            },
            {
                step: '3',
                titleKey: 'Start creating',
                descKey: 'Pro account. Referral link. Full access. Go.',
                color: 'bg-emerald-500',
            },
        ].map((s) => (<div key={s.step} className='relative'>
                {/* Step circle */}
                <div className={clsxm('mb-5 flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold text-white', s.color)}>
                  {s.step}
                </div>
                <h3 className='mb-2 text-lg font-semibold text-neutral-900 dark:text-white'>
                  {t(s.titleKey)}
                </h3>
                <p className='text-[15px] leading-relaxed text-neutral-500 dark:text-neutral-400'>
                  {t(s.descKey)}
                </p>
              </div>))}
          </div>
        </div>
      </section>

      {/* ━━━ CREATOR SPOTLIGHT ━━━ */}
      <CreatorSpotlight />

      {/* ━━━ APPLICATION FORM ━━━ */}
      <section className='relative border-y border-neutral-200 bg-neutral-50 py-24 dark:border-neutral-800 dark:bg-neutral-900/30 sm:py-32'>
        <div className='mx-auto max-w-xl px-5 sm:px-8'>
          <div ref={formRef} className='scroll-mt-32'>
            <div className='mb-10 text-center'>
              <h2 className='text-2xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-3xl'>
                {t('Apply to the Creator Program')}
              </h2>
              <p className='mt-3 text-neutral-500 dark:text-neutral-400'>
                {t('30 seconds. We review every application.')}
              </p>
            </div>

            {submitStatus === 'success' ? (<div className='rounded-2xl border border-neutral-200 bg-white px-8 py-12 text-center dark:border-neutral-800 dark:bg-[#0c0c0c]'>
                <div className='mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30'>
                  <svg className='h-6 w-6 text-emerald-600 dark:text-emerald-400' fill='none' viewBox='0 0 24 24' strokeWidth={2.5} stroke='currentColor'>
                    <path strokeLinecap='round' strokeLinejoin='round' d='M4.5 12.75l6 6 9-13.5'/>
                  </svg>
                </div>
                <h3 className='text-xl font-semibold text-neutral-900 dark:text-white'>
                  {t("You're in the queue")}
                </h3>
                <p className='mt-2 text-neutral-500 dark:text-neutral-400'>
                  {t("We'll review your application and get back to you within 48 hours.")}
                </p>
              </div>) : (<form onSubmit={handleSubmit(onSubmit)} className='space-y-5' noValidate>
                {/* Email */}
                <div>
                  <label htmlFor='creator-email' className='mb-2 block text-sm font-medium text-neutral-700 dark:text-neutral-300'>
                    {t('Email address')}
                  </label>
                  <input id='creator-email' {...register('email', {
            required: t('Please enter your email address'),
            pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: t('Please enter a valid email address'),
            },
        })} type='email' autoComplete='email' className={clsxm('w-full rounded-xl border bg-white px-4 py-3.5 text-neutral-900 outline-none transition-shadow placeholder:text-neutral-400 focus:ring-2 dark:bg-[#0c0c0c] dark:text-white dark:placeholder:text-neutral-600', errors.email
                ? 'border-red-400 focus:ring-red-200 dark:border-red-500/50 dark:focus:ring-red-500/20'
                : 'border-neutral-200 focus:border-neutral-400 focus:ring-neutral-200 dark:border-neutral-800 dark:focus:border-neutral-600 dark:focus:ring-neutral-800')} placeholder='you@example.com'/>
                  {errors.email && (<p role='alert' className='mt-1.5 text-sm text-red-600 dark:text-red-400'>
                      {errors.email.message}
                    </p>)}
                </div>

                {/* Social Links */}
                <div>
                  <label htmlFor='creator-links' className='mb-2 block text-sm font-medium text-neutral-700 dark:text-neutral-300'>
                    {t('Your content links')}
                  </label>
                  <textarea id='creator-links' {...register('socialLinks')} rows={4} className='w-full rounded-xl border border-neutral-200 bg-white px-4 py-3.5 text-neutral-900 outline-none transition-shadow placeholder:text-neutral-400 focus:border-neutral-400 focus:ring-2 focus:ring-neutral-200 dark:border-neutral-800 dark:bg-[#0c0c0c] dark:text-white dark:placeholder:text-neutral-600 dark:focus:border-neutral-600 dark:focus:ring-neutral-800' placeholder={t('YouTube, TikTok, Instagram, blog — one link per line')}/>
                </div>

                <Turnstile ref={turnstileRef} action={CREATOR_APPLICATION_TURNSTILE_ACTION} context='contact'/>

                {/* Submit */}
                <button type='submit' disabled={isLoading} className='flex w-full items-center justify-center gap-2 rounded-xl bg-neutral-900 py-3.5 text-[15px] font-semibold text-white transition-all hover:bg-neutral-800 active:scale-[.98] disabled:opacity-60 dark:bg-white dark:text-neutral-900 dark:hover:bg-neutral-200'>
                  {isLoading ? (<>
                      <svg className='h-4 w-4 animate-spin' viewBox='0 0 24 24' fill='none'>
                        <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'/>
                        <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'/>
                      </svg>
                      {t('Sending...')}
                    </>) : (t('Submit application'))}
                </button>

                {(submitStatus === 'error' || submitStatus === 'captcha') && (<p role='alert' className='text-center text-sm text-red-600 dark:text-red-400'>
                    {submitStatus === 'captcha'
                    ? t('Please complete the security check and try again.')
                    : t('Something went wrong. Please try again.')}
                  </p>)}
              </form>)}
          </div>
        </div>
      </section>

      {/* ━━━ FAQ ━━━ */}
      <section className='relative py-24 sm:py-32'>
        <div className='mx-auto max-w-2xl px-5 sm:px-8'>
          <h2 className='mb-12 text-2xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-3xl'>
            {t('Questions & answers')}
          </h2>

          <div className='divide-y divide-neutral-200 border-y border-neutral-200 dark:divide-neutral-800 dark:border-neutral-800'>
            {faqs.map((faq, i) => (<div key={i}>
                <button type='button' onClick={() => setOpenFaq(openFaq === i ? null : i)} className='flex w-full items-center justify-between py-5 text-left transition-colors' aria-expanded={openFaq === i}>
                  <span className='pr-8 text-[15px] font-medium text-neutral-900 dark:text-white'>
                    {t(faq.q)}
                  </span>
                  <span className={clsxm('flex h-6 w-6 shrink-0 items-center justify-center text-neutral-400 transition-transform duration-200', openFaq === i && 'rotate-45')}>
                    <svg width='14' height='14' viewBox='0 0 14 14' fill='none' stroke='currentColor' strokeWidth='1.5'>
                      <line x1='7' y1='0' x2='7' y2='14'/>
                      <line x1='0' y1='7' x2='14' y2='7'/>
                    </svg>
                  </span>
                </button>
                <div className={clsxm('grid transition-all duration-200 ease-out', openFaq === i
                ? 'grid-rows-[1fr] pb-5 opacity-100'
                : 'grid-rows-[0fr] opacity-0')}>
                  <div className='overflow-hidden'>
                    <p className='text-[15px] leading-relaxed text-neutral-500 dark:text-neutral-400'>
                      {t(faq.a)}
                    </p>
                  </div>
                </div>
              </div>))}
          </div>
        </div>
      </section>

      {/* ━━━ FINAL CTA ━━━ */}
      <section className='relative overflow-hidden border-t border-neutral-200 py-24 dark:border-neutral-800 sm:py-32'>
        {/* Subtle bg color */}
        <div className='pointer-events-none absolute inset-0'>
          <div className='absolute left-1/2 top-1/2 h-[500px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-violet-100 opacity-30 blur-[120px] dark:bg-violet-900/20'/>
        </div>
        <div className='relative mx-auto max-w-2xl px-5 text-center sm:px-8'>
          <h2 className='text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl'>
            {t('Ready to create?')}
          </h2>
          <p className='mt-4 text-lg text-neutral-500 dark:text-neutral-400'>
            {t('Join hundreds of creators already using Easy-Peasy.AI to make incredible content.')}
          </p>
          <button onClick={scrollToForm} className='mt-8 rounded-full bg-neutral-900 px-8 py-3.5 text-[15px] font-semibold text-white transition-all hover:shadow-lg hover:shadow-neutral-900/20 active:scale-[.97] dark:bg-white dark:text-neutral-900 dark:hover:shadow-white/10'>
            {t('Apply now')}
          </button>
        </div>
      </section>

      <Footer />
    </main>);
}
async function getStaticProps() {
    return {
        props: {},
    };
}

    async function __Next_Translate__getStaticProps__1a12e86ef9a__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/creators',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a12e86ef9a__ as getStaticProps }
  